import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryButton from '../components/PrimaryButton';
import { PhotoIcon, FolderPlusIcon, ArrowUpTrayIcon, TrashIcon, PencilIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon, FunnelIcon } from '@heroicons/react/24/outline';

const Admin = () => {
    const [collections, setCollections] = useState([]);
    const [newCollection, setNewCollection] = useState({ name: '', description: '' });

    // Struttura stato per gestire l'upload avanzato
    const [uploadForm, setUploadForm] = useState({
        title: '',
        collection_id: '',
        coverFile: null,
        otherFiles: []
    });

    const [isLoading, setIsLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0); // Percentuale avanzamento upload
    const [message, setMessage] = useState({ type: '', text: '' });

    // Stati per la gestione delle immagini esistenti e paginazione
    const [images, setImages] = useState([]);
    const [isImagesLoading, setIsImagesLoading] = useState(false);
    const [imagePagination, setImagePagination] = useState({ page: 1, total_pages: 1 });

    // Paginazione Orizzontale per la sezione "Existing Collections"
    const [collectionPage, setCollectionPage] = useState(0);
    const collectionsPerPage = 3;

    // Stato per il filtraggio della tabella immagini per collezione
    const [filterCollectionId, setFilterCollectionId] = useState('');

    // Stati per la gestione del Dialog di Modifica Collezioni
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editingCollection, setEditingCollection] = useState({ id: '', name: '', description: '' });

    // Stati per la gestione del Dialog di Modifica Immagini
    const [isImageEditOpen, setIsImageEditOpen] = useState(false);
    const [editingImage, setEditingImage] = useState({ id: '', title: '', collection_id: '' });

    // Stati per l'anteprima (Lightbox) della miniatura
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewImageId, setPreviewImageId] = useState(null);

    const navigate = useNavigate();
    const [token, setToken] = useState(null);

    const API_URL = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:8080');

    const fetchCollections = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/collections`);
            if (response.status === 204) {
                setCollections([]);
                return;
            }
            if (response.ok) {
                const data = await response.json();
                setCollections(data || []);
            } else {
                throw new Error('Failed to fetch collections');
            }
        } catch (error) {
            console.error('Error fetching collections:', error);
            setCollections([]);
        }
    }, [API_URL]);

    const fetchImages = useCallback(async (page = 1, collectionId = '') => {
        setIsImagesLoading(true);
        try {
            let url = '';
            if (collectionId) {
                url = `${API_URL}/collections/${collectionId}/images?page=${page}&limit=5`;
            } else {
                url = `${API_URL}/images/?page=${page}&limit=5`;
            }

            const response = await fetch(url);
            if (response.status === 204) {
                setImages([]);
                setImagePagination({ page: 1, total_pages: 1 });
                return;
            }
            if (response.ok) {
                const data = await response.json();

                const processedImages = (data.data || []).map(item => ({
                    id: item.id,
                    title: item.title || `Photo #${item.id}`,
                    collection_id: item.collection_id || ''
                }));

                setImages(processedImages);
                setImagePagination({ page: data.page, total_pages: data.total_pages });
            }
        } catch (error) {
            console.error('Error fetching images:', error);
        } finally {
            setIsImagesLoading(false);
        }
    }, [API_URL]);

    useEffect(() => {
        const currentToken = localStorage.getItem('token');
        if (!currentToken) {
            navigate('/login');
            return;
        } else {
            setToken(currentToken);
        }

        fetchCollections();
        fetchImages(1, filterCollectionId);
    }, [navigate, fetchCollections, fetchImages, filterCollectionId]);

    const handleFilterChange = (e) => {
        const selectedId = e.target.value;
        setFilterCollectionId(selectedId);
        fetchImages(1, selectedId);
    };

    const handleCreateCollection = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await fetch(`${API_URL}/admin/collections`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newCollection)
            });

            if (response.ok) {
                setMessage({ type: 'success', text: 'Collection created successfully!' });
                setNewCollection({ name: '', description: '' });
                fetchCollections();
            } else {
                const data = await response.json();
                throw new Error(data.error || 'Failed to create collection');
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleUploadImage = async (e) => {
        e.preventDefault();
        if (!uploadForm.coverFile && uploadForm.otherFiles.length === 0) {
            setMessage({ type: 'error', text: 'Please select at least one file to upload (Cover or Others)' });
            return;
        }

        setIsLoading(true);
        setUploadProgress(0);
        setMessage({ type: '', text: '' });

        try {
            let nextIndex = 1;

            if (uploadForm.collection_id) {
                const checkRes = await fetch(`${API_URL}/collections/${uploadForm.collection_id}/images?page=1&limit=1`);
                if (checkRes.ok && checkRes.status !== 204) {
                    const checkData = await checkRes.json();
                    if (checkData.total_elements && checkData.total_elements > 0) {
                        nextIndex = checkData.total_elements + 1;
                    }
                }
            }

            const uploadQueue = [];

            if (uploadForm.coverFile) {
                uploadQueue.push({
                    file: uploadForm.coverFile,
                    title: uploadForm.collection_id ? String(nextIndex) : uploadForm.title || "Cover"
                });
                nextIndex++;
            }

            if (uploadForm.otherFiles && uploadForm.otherFiles.length > 0) {
                Array.from(uploadForm.otherFiles).forEach((file, i) => {
                    uploadQueue.push({
                        file: file,
                        title: uploadForm.collection_id ? String(nextIndex + i) : `${uploadForm.title || "Photo"} _${i + 1}`
                    });
                });
            }

            const totalFiles = uploadQueue.length;

            for (let index = 0; index < totalFiles; index++) {
                const item = uploadQueue[index];
                const formData = new FormData();
                formData.append('image', item.file);
                formData.append('title', item.title);
                formData.append('collection_id', uploadForm.collection_id);

                const response = await fetch(`${API_URL}/admin/images`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData
                });

                if (!response.ok) {
                    throw new Error(`Failed during file ${index + 1}/${totalFiles}. Upload interrupted.`);
                }

                setUploadProgress(Math.round(((index + 1) / totalFiles) * 100));
            }

            setMessage({ type: 'success', text: `All ${totalFiles} images uploaded successfully with dynamic indexing!` });
            setUploadForm({ title: '', collection_id: '', coverFile: null, otherFiles: [] });
            e.target.reset();
            fetchCollections();
            fetchImages(1, filterCollectionId);
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setIsLoading(false);
            setUploadProgress(0);
        }
    };

    const handleUpdateCollection = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await fetch(`${API_URL}/admin/collections/${editingCollection.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: editingCollection.name,
                    description: editingCollection.description
                })
            });

            if (response.ok) {
                setMessage({ type: 'success', text: 'Collection updated successfully!' });
                setIsEditOpen(false);
                fetchCollections();
            } else {
                const data = await response.json();
                throw new Error(data.error || 'Failed to update collection');
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateImage = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await fetch(`${API_URL}/admin/images/${editingImage.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    title: editingImage.title,
                    collection_id: editingImage.collection_id ? Number(editingImage.collection_id) : 0
                })
            });

            if (response.ok) {
                setMessage({ type: 'success', text: 'Photo updated successfully!' });
                setIsImageEditOpen(false);
                fetchImages(imagePagination.page, filterCollectionId);
                fetchCollections();
            } else {
                const data = await response.json();
                throw new Error(data.error || 'Failed to update photo');
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteCollection = async (id) => {
        if (!window.confirm('Are you sure? This will delete the collection.')) return;

        try {
            const response = await fetch(`${API_URL}/admin/collections/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                fetchCollections();
                setCollectionPage(0);
                setMessage({ type: 'success', text: 'Collection deleted' });
            } else {
                const data = await response.json();
                setMessage({ type: 'error', text: data.error || 'Failed to delete collection' });
            }
        } catch (error) {
            console.error('Delete failed:', error);
            setMessage({ type: 'error', text: error.message });
        }
    };

    const handleDeleteImage = async (id) => {
        if (!window.confirm('Are you sure you want to permanently delete this photo?')) return;

        try {
            const response = await fetch(`${API_URL}/admin/images/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setMessage({ type: 'success', text: 'Photo deleted successfully' });
                const pageToFetch = images.length === 1 && imagePagination.page > 1 ? imagePagination.page - 1 : imagePagination.page;
                fetchImages(pageToFetch, filterCollectionId);
                fetchCollections();
            } else {
                const data = await response.json();
                setMessage({ type: 'error', text: data.error || 'Failed to delete photo' });
            }
        } catch (error) {
            console.error('Delete photo failed:', error);
            setMessage({ type: 'error', text: error.message });
        }
    };

    const openEditModal = (collection) => {
        setEditingCollection({
            id: collection.id,
            name: collection.name,
            description: collection.description || ''
        });
        setIsEditOpen(true);
    };

    const openImageEditModal = (img) => {
        setEditingImage({
            id: img.id,
            title: img.title.startsWith('Photo #') ? '' : img.title,
            collection_id: img.collection_id ? String(img.collection_id) : ''
        });
        setIsImageEditOpen(true);
    };

    const openPreviewModal = (id) => {
        setPreviewImageId(id);
        setIsPreviewOpen(true);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    const getCollectionName = (colId) => {
        const col = collections.find(c => c.id === colId);
        return col ? col.name : 'Unassigned';
    };

    const visibleCollections = collections.slice(
        collectionPage * collectionsPerPage,
        (collectionPage + 1) * collectionsPerPage
    );

    const totalCollectionPages = Math.ceil(collections.length / collectionsPerPage);

    return (
        <main className="min-h-screen bg-earth-50 dark:bg-earth-900 pt-28 pb-12 px-4 lg:px-8">
            <div className="max-w-6xl mx-auto">
                <header className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <h1 className="text-4xl font-bold text-earth-900 dark:text-earth-50">Dashboard Admin</h1>
                        <p className="text-earth-700 dark:text-earth-200 mt-2">Manage your photography collections and uploads.</p>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="text-sm font-semibold text-earth-600 dark:text-earth-400 hover:text-earth-900 dark:hover:text-earth-50 transition-colors"
                    >
                        Sign Out
                    </button>
                </header>

                {message.text && (
                    <div className={`mb-8 p-4 rounded-xl text-center font-medium ${
                        message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                        {message.text}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Sezione Creazione Collezioni */}
                    <section className="bg-earth-100 dark:bg-earth-800 p-6 rounded-2xl shadow-sm border border-earth-200 dark:border-earth-700">
                        <div className="flex items-center gap-2 mb-6">
                            <FolderPlusIcon className="h-6 w-6 text-earth-600 dark:text-earth-400" />
                            <h2 className="text-xl font-semibold text-earth-900 dark:text-earth-50">New Collection</h2>
                        </div>
                        <form onSubmit={handleCreateCollection} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-earth-800 dark:text-earth-200 mb-1">Name</label>
                                <input
                                    type="text"
                                    required
                                    value={newCollection.name}
                                    onChange={(e) => setNewCollection({ ...newCollection, name: e.target.value })}
                                    className="w-full px-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-900 dark:text-earth-50 outline-none focus:ring-2 focus:ring-earth-500"
                                    placeholder="e.g. Nature 2026"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-earth-800 dark:text-earth-200 mb-1">Description</label>
                                <textarea
                                    value={newCollection.description}
                                    onChange={(e) => setNewCollection({ ...newCollection, description: e.target.value })}
                                    className="w-full px-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-900 dark:text-earth-50 outline-none focus:ring-2 focus:ring-earth-500 min-h-[100px]"
                                    placeholder="Short description..."
                                />
                            </div>
                            <PrimaryButton type="submit" className="w-full justify-center" disabled={isLoading}>
                                Create Collection
                            </PrimaryButton>
                        </form>
                    </section>

                    {/* Sezione Form Caricamento Foto Batch */}
                    <section className="bg-earth-100 dark:bg-earth-800 p-6 rounded-2xl shadow-sm border border-earth-200 dark:border-earth-700">
                        <div className="flex items-center gap-2 mb-6">
                            <ArrowUpTrayIcon className="h-6 w-6 text-earth-600 dark:text-earth-400" />
                            <h2 className="text-xl font-semibold text-earth-900 dark:text-earth-50">Upload Photo Batch</h2>
                        </div>
                        <form onSubmit={handleUploadImage} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-earth-800 dark:text-earth-200 mb-1">Base Title (Fallback)</label>
                                <input
                                    type="text"
                                    value={uploadForm.title}
                                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                                    className="w-full px-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-900 dark:text-earth-50 outline-none focus:ring-2 focus:ring-earth-500"
                                    placeholder="Fallback title if no collection selected"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-earth-800 dark:text-earth-200 mb-1">Collection (Optional)</label>
                                <select
                                    value={uploadForm.collection_id}
                                    onChange={(e) => setUploadForm({ ...uploadForm, collection_id: e.target.value })}
                                    className="w-full px-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-900 dark:text-earth-50 outline-none focus:ring-2 focus:ring-earth-500 cursor-pointer"
                                >
                                    <option value="">Leave unassigned (No collection)</option>
                                    {collections.map(col => (
                                        <option key={col.id} value={col.id}>{col.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-earth-800 dark:text-earth-200 mb-1">Cover Image</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => setUploadForm({ ...uploadForm, coverFile: e.target.files[0] })}
                                    className="w-full px-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-800 dark:text-earth-200 text-sm file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-earth-200 file:text-earth-800 hover:file:bg-earth-300 cursor-pointer outline-none focus:ring-2 focus:ring-earth-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-earth-800 dark:text-earth-200 mb-1">Other Gallery Files</label>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={(e) => setUploadForm({ ...uploadForm, otherFiles: e.target.files })}
                                    className="w-full px-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-800 dark:text-earth-200 text-sm file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-earth-200 file:text-earth-800 hover:file:bg-earth-300 cursor-pointer outline-none focus:ring-2 focus:ring-earth-500"
                                />
                            </div>

                            {/* MODIFICATO: Inserita una barra di caricamento distaccata subito sopra il pulsante */}
                            {isLoading && (
                                <div className="pt-2 animate-fade-in">
                                    <div className="flex justify-between text-xs font-semibold text-earth-700 dark:text-earth-300 mb-1">
                                        <span>Uploading Batch</span>
                                        <span>{uploadProgress}%</span>
                                    </div>
                                    <div className="w-full bg-earth-200 dark:bg-earth-700 h-2 rounded-full overflow-hidden">
                                        <div
                                            className="bg-green-600 dark:bg-green-500 h-full transition-all duration-300 ease-out rounded-full"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* MODIFICATO: Sostituito il pulsante vecchio con il componente <PrimaryButton> */}
                            <PrimaryButton
                                type="submit"
                                disabled={isLoading}
                                className="w-full justify-center flex items-center gap-2 mt-2"
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Uploading...</span>
                                    </>
                                ) : (
                                    <>
                                        <ArrowUpTrayIcon className="h-5 w-5" />
                                        <span>Upload Image Batch</span>
                                    </>
                                )}
                            </PrimaryButton>
                        </form>
                    </section>
                </div>

                {/* Sezione Liste Collezioni Esistenti con Paginazione Orizzontale a Carosello */}
                <section className="mt-12 bg-earth-100 dark:bg-earth-800 p-6 rounded-2xl shadow-sm border border-earth-200 dark:border-earth-700">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                        <div className="flex items-center gap-2">
                            <PhotoIcon className="h-6 w-6 text-earth-600 dark:text-earth-400" />
                            <h2 className="text-xl font-semibold text-earth-900 dark:text-earth-50">Existing Collections</h2>
                        </div>

                        {totalCollectionPages > 1 && (
                            <div className="flex items-center justify-end gap-2 text-sm font-medium bg-earth-50 dark:bg-earth-900 px-3 py-1.5 rounded-xl border border-earth-300 dark:border-earth-700 shadow-sm self-end sm:self-auto">
                                <button
                                    disabled={collectionPage === 0}
                                    onClick={() => setCollectionPage(prev => Math.max(0, prev - 1))}
                                    className="p-1 hover:bg-earth-200 dark:hover:bg-earth-800 text-earth-700 dark:text-earth-200 rounded-lg disabled:opacity-30 transition-all"
                                >
                                    <ChevronLeftIcon className="h-4 w-4" />
                                </button>
                                <span className="text-earth-800 dark:text-earth-200 px-1 whitespace-nowrap">
                                    {collectionPage + 1} / {totalCollectionPages}
                                </span>
                                <button
                                    disabled={collectionPage === totalCollectionPages - 1}
                                    onClick={() => setCollectionPage(prev => Math.min(totalCollectionPages - 1, prev + 1))}
                                    className="p-1 hover:bg-earth-200 dark:hover:bg-earth-800 text-earth-700 dark:text-earth-200 rounded-lg disabled:opacity-30 transition-all"
                                >
                                    <ChevronRightIcon className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                    </div>

                    {collections.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 transition-all duration-300">
                            {visibleCollections.map(col => (
                                <div key={col.id} className="p-4 border border-earth-200 dark:border-earth-700 rounded-xl flex items-center justify-between bg-earth-50 dark:bg-earth-900 hover:bg-earth-200 dark:hover:bg-earth-750 transition-colors animate-fade-in">
                                    <div>
                                        <h3 className="font-medium text-earth-900 dark:text-earth-50">{col.name}</h3>
                                        <p className="text-xs text-earth-600 dark:text-earth-400">{col.images_count || 0} photos</p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => openEditModal(col)}
                                            className="p-2 text-earth-500 hover:text-earth-800 dark:hover:text-earth-200 transition-colors"
                                            title="Edit Collection"
                                        >
                                            <PencilIcon className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteCollection(col.id)}
                                            className="p-2 text-earth-500 hover:text-red-600 transition-colors"
                                            title="Delete Collection"
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-earth-500 dark:text-earth-400 text-sm border-2 border-dashed border-earth-200 dark:border-earth-700 rounded-xl bg-earth-50/50">
                            No collections available. Create one using the form above.
                        </div>
                    )}
                </section>

                {/* Existing Photos List */}
                <section className="mt-12 bg-earth-100 dark:bg-earth-800 p-6 rounded-2xl shadow-sm border border-earth-200 dark:border-earth-700">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-earth-200 dark:border-earth-700">
                        <div className="flex items-center gap-2">
                            <PhotoIcon className="h-6 w-6 text-earth-600 dark:text-earth-400" />
                            <h2 className="text-xl font-semibold text-earth-900 dark:text-earth-50">Existing Photos</h2>
                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-4 flex-1 max-w-2xl">
                            <div className="relative flex items-center flex-1 sm:max-w-xs">
                                <FunnelIcon className="absolute left-3 h-4 w-4 text-earth-400 pointer-events-none" />
                                <select
                                    value={filterCollectionId}
                                    onChange={handleFilterChange}
                                    className="w-full pl-9 pr-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-900 dark:text-earth-50 text-sm outline-none focus:ring-2 focus:ring-earth-500 transition-all appearance-none cursor-pointer font-medium"
                                >
                                    <option value="">All Collections</option>
                                    {collections.map(col => (
                                        <option key={col.id} value={col.id}>
                                            {col.name} ({col.images_count || 0})
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute right-3 pointer-events-none text-earth-400 text-xs">▼</div>
                            </div>

                            {imagePagination.total_pages > 1 && (
                                <div className="flex items-center justify-center gap-2 text-sm font-medium bg-earth-50 dark:bg-earth-900 px-3 py-1.5 rounded-xl border border-earth-300 dark:border-earth-700 shadow-sm">
                                    <button
                                        disabled={imagePagination.page === 1 || isImagesLoading}
                                        onClick={() => fetchImages(imagePagination.page - 1, filterCollectionId)}
                                        className="p-1 hover:bg-earth-200 dark:hover:bg-earth-800 text-earth-700 dark:text-earth-200 rounded-lg disabled:opacity-40 transition-all"
                                    >
                                        <ChevronLeftIcon className="h-4 w-4" />
                                    </button>
                                    <span className="text-earth-800 dark:text-earth-200 px-1 whitespace-nowrap">
                                        {imagePagination.page} / {imagePagination.total_pages}
                                    </span>
                                    <button
                                        disabled={imagePagination.page === imagePagination.total_pages || isImagesLoading}
                                        onClick={() => fetchImages(imagePagination.page + 1, filterCollectionId)}
                                        className="p-1 hover:bg-earth-200 dark:hover:bg-earth-800 text-earth-700 dark:text-earth-200 rounded-lg disabled:opacity-40 transition-all"
                                    >
                                        <ChevronRightIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {isImagesLoading ? (
                        <div className="space-y-3 animate-pulse">
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="h-16 bg-earth-50 dark:bg-earth-900 rounded-xl" />
                            ))}
                        </div>
                    ) : images.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-earth-200 dark:divide-earth-700 table-fixed">
                                <thead>
                                    <tr className="text-left text-xs font-semibold text-earth-600 dark:text-earth-400 uppercase tracking-wider">
                                        <th className="pb-3 w-20">Preview</th>
                                        <th className="pb-3 w-1/3">Title</th>
                                        <th className="pb-3 w-1/3">Collection</th>
                                        <th className="pb-3 w-24 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-earth-200 dark:divide-earth-700">
                                    {images.map(img => (
                                        <tr key={img.id} className="hover:bg-earth-50 dark:hover:bg-earth-900/50 transition-colors group">
                                            <td className="py-3 px-4 align-middle">
                                                {img.id ? (
                                                    <img
                                                        src={`${API_URL}/images/${img.id}/lq`}
                                                        alt={img.title || "Preview"}
                                                        onClick={() => openPreviewModal(img.id)}
                                                        className="h-12 w-12 rounded-lg object-cover bg-earth-200 dark:bg-earth-900 border border-earth-200 dark:border-earth-700 shadow-sm cursor-zoom-in hover:scale-105 transition-transform"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <div className="h-12 w-12 rounded-lg bg-earth-200 dark:bg-earth-700 animate-pulse flex-shrink-0" />
                                                )}
                                            </td>

                                            <td className="py-3 pr-4 align-middle font-medium text-sm text-earth-900 dark:text-earth-50 truncate">
                                                {img.title || `Photo #${img.id}`}
                                            </td>

                                            <td className="py-3 pr-4 align-middle text-sm text-earth-700 dark:text-earth-300 truncate">
                                                {getCollectionName(img.collection_id)}
                                            </td>

                                            <td className="py-3 text-right align-middle whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        onClick={() => openImageEditModal(img)}
                                                        className="p-2 text-earth-500 hover:text-earth-800 dark:hover:text-earth-200 transition-colors rounded-lg hover:bg-earth-200 dark:hover:bg-earth-800"
                                                        title="Edit Image Details"
                                                    >
                                                        <PencilIcon className="h-5 w-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteImage(img.id)}
                                                        className="p-2 text-earth-500 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30"
                                                        title="Delete Photo"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-12 text-earth-500 dark:text-earth-400 text-sm border-2 border-dashed border-earth-200 dark:border-earth-700 rounded-xl bg-earth-50/50 dark:bg-earth-900/30">
                            {filterCollectionId
                                ? "No photos found inside this specific collection."
                                : "No photos uploaded yet. Use the form above to add your first capture."
                            }
                        </div>
                    )}
                </section>
            </div>

            {/* Dialog / Modal per Modifica Collezione */}
            {isEditOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="bg-earth-100 dark:bg-earth-800 w-full max-w-md p-6 rounded-2xl shadow-xl border border-earth-200 dark:border-earth-700 relative">
                        <button
                            onClick={() => setIsEditOpen(false)}
                            className="absolute top-4 right-4 text-earth-500 hover:text-earth-800 dark:hover:text-earth-200 transition-colors"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>

                        <div className="flex items-center gap-2 mb-6">
                            <PencilIcon className="h-6 w-6 text-earth-600 dark:text-earth-400" />
                            <h2 className="text-xl font-semibold text-earth-900 dark:text-earth-50">Edit Collection</h2>
                        </div>

                        <form onSubmit={handleUpdateCollection} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-earth-800 dark:text-earth-200 mb-1">Name</label>
                                <input
                                    type="text"
                                    required
                                    value={editingCollection.name}
                                    onChange={(e) => setEditingCollection({ ...editingCollection, name: e.target.value })}
                                    className="w-full px-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-900 dark:text-earth-50 outline-none focus:ring-2 focus:ring-earth-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-earth-800 dark:text-earth-200 mb-1">Description</label>
                                <textarea
                                    value={editingCollection.description}
                                    onChange={(e) => setEditingCollection({ ...editingCollection, description: e.target.value })}
                                    className="w-full px-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-900 dark:text-earth-50 outline-none focus:ring-2 focus:ring-earth-500 min-h-[100px]"
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsEditOpen(false)}
                                    className="flex-1 px-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl text-earth-800 dark:text-earth-200 font-semibold hover:bg-earth-50 dark:hover:bg-earth-900 transition-colors text-sm"
                                >
                                    Cancel
                                </button>
                                <PrimaryButton type="submit" className="flex-1 justify-center" disabled={isLoading}>
                                    Save Changes
                                </PrimaryButton>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Dialog / Modal per Modifica Dettagli Immagine */}
            {isImageEditOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-earth-100 dark:bg-earth-800 w-full max-w-md p-6 rounded-2xl shadow-xl border border-earth-200 dark:border-earth-700 relative">
                        <button
                            onClick={() => setIsImageEditOpen(false)}
                            className="absolute top-4 right-4 text-earth-500 hover:text-earth-800 dark:hover:text-earth-200 transition-colors"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>

                        <div className="flex items-center gap-2 mb-6">
                            <PencilIcon className="h-6 w-6 text-earth-600 dark:text-earth-400" />
                            <h2 className="text-xl font-semibold text-earth-900 dark:text-earth-50">Edit Photo Details</h2>
                        </div>

                        <form onSubmit={handleUpdateImage} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-earth-800 dark:text-earth-200 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={editingImage.title}
                                    onChange={(e) => setEditingImage({ ...editingImage, title: e.target.value })}
                                    className="w-full px-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-900 dark:text-earth-50 outline-none focus:ring-2 focus:ring-earth-500"
                                    placeholder="Leave empty or type new title"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-earth-800 dark:text-earth-200 mb-1">Collection</label>
                                <select
                                    value={editingImage.collection_id}
                                    onChange={(e) => setEditingImage({ ...editingImage, collection_id: e.target.value })}
                                    className="w-full px-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-900 dark:text-earth-50 outline-none focus:ring-2 focus:ring-earth-500"
                                >
                                    <option value="">Unassigned / No collection</option>
                                    {collections.map(col => (
                                        <option key={col.id} value={col.id}>{col.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsImageEditOpen(false)}
                                    className="flex-1 px-4 py-2 border border-earth-300 dark:border-earth-700 rounded-xl text-earth-800 dark:text-earth-200 font-semibold hover:bg-earth-50 dark:hover:bg-earth-900 transition-colors text-sm"
                                >
                                    Cancel
                                </button>
                                <PrimaryButton type="submit" className="flex-1 justify-center" disabled={isLoading}>
                                    Save Changes
                                </PrimaryButton>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Dialog / Lightbox per Anteprima Miniatura */}
            {isPreviewOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm cursor-zoom-out"
                    onClick={() => setIsPreviewOpen(false)}
                >
                    <div
                        className="relative max-w-4xl max-h-[85vh] overflow-hidden rounded-2xl bg-black border border-earth-700/50 p-0 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setIsPreviewOpen(false)}
                            className="absolute top-4 right-4 z-10 p-2 bg-black/60 rounded-full text-white hover:text-earth-300 hover:bg-black/80 transition-colors"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>

                        <img
                            src={`${API_URL}/images/${previewImageId}/lq`}
                            alt="Lightbox Preview"
                            className="w-full h-auto max-h-[85vh] object-contain select-none block"
                        />
                    </div>
                </div>
            )}
        </main>
    );
};

export default Admin;